import { useEffect, useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../Modal/Modal";
import Tabs from "../Tabs/Tabs";
import Flex from "../Flex/Flex";
import Checkbox from "../forms/Checkbox/Checkbox";
import ProductImage from "../ProductImage/ProductImage";
import CopyToClipBoard from "../CopyToClipboard/CopyToClipboard";
import Button from "../forms/Button/Button";
import InputNumber from "../forms/InputNumber/InputNumber";
import { PaperSize, PrinterDetails, QzConnectionState, usePrinterContext } from "@/contexts/PrinterContext";
import { Tooltip } from "../Tooltip/Tooltip";
import { BoxemPrintRow, FNSKUPrinterSettings } from "@/app/settings/printing/page";
import RadioInput from "../forms/RadioInput";
import Alert from "../Alert/Alert";
import Divider from "../Divider/Divider";
import { base64SoldAsSetStickerPDF } from "@/contexts/pdf/sold-as-set-generation";
import Select from "../forms/Select/Select";
import { getLocalStorageItem, LocalStorageKeys, setLocalStorageItem } from "@/utils/localStorageUtils";
import IconPrintGoogle from "../icons/IconPrintGoogle";
import { base64SSuffocationStickerPDF } from "@/contexts/pdf/suffocation-sticker-generation";
import { base64FragileLabelPDF } from "@/contexts/pdf/fragile-sticker-generation";
import { base64SimpleLabelPDF, SimpleLabelGenerationOptions } from "@/contexts/pdf/simple-warning-label-generation";
import Textarea from "../forms/Textarea/Textarea";
import Accordion from "../Accordion/Accordion";
import TextInput from "../forms/TextInput/TextInput";
import { ProductCondition } from "@boxem/core-api-schema";
import DatePicker from "../forms/DatePicker";
import { productConditionToString } from "@/app/fba/utils";
import base64FNSKULabelPdf, { base64FNSKULabelPDFMulti, FNSKULabelSpecWithQuantity } from "@/contexts/pdf/fnsku-label-generation";

import styles from "./LabelsModal.module.css";
import ProductView from "@/app/fba/FbaShipmentFlow/ProductView";
import { FbaBatchItem } from "@/contexts/ShipmentContext";


export interface LabelsModalProps {
  open: boolean;
  onClose: () => void;

  items: LabelsModalItem[];
}

export interface LabelsModalItem {
  batchItem: FbaBatchItem;
  fnsku: string;
  totalQuantity: number;
  assignedQuantity?: number;
}

export function LabelsModal(props: LabelsModalProps) {
  const { open, onClose, items } = props;

  const [tab, setTab] = useState<LabelsModalTab>("fnsku");

  return (
    <Modal
      className={styles.modal}
      open={open}
      onOpenChange={o => { if (!o) onClose(); }}
      closeOnOutsideClick={false}
    >
      <ModalHeader className={styles.header}>
        <Tabs
          noBorder
          tabs={[
            {
              key: "fnsku",
              label: "Print FNSKU"
            },
            {
              key: "settings",
              label: "Printer Settings",
            },
            {
              key: "other_labels",
              label: "Other Labels",
            }
          ]}
          activeTab={tab}
          setActiveTab={t => setTab(t as LabelsModalTab)}
        />
      </ModalHeader>

      <ModalBody className={styles.body}>
        {tab === "fnsku"        && <FNSKUTabBody items={items} onClose={() => onClose() }/>}
        {tab === "settings"     && <SettingsBody/>}
        {tab === "other_labels" && <OtherLabelsBody/>}
      </ModalBody>
    </Modal>
  );
}

type LabelsModalTab = "fnsku" | "settings" | "other_labels";

function FNSKUTabBody(props: { items: LabelsModalItem[], onClose: () => void }) {
  const { items, onClose } = props;

  const { printBase64PDF, submitJobInProgress, preferredFNSKULabelSize, preferredFNSKULabelPrinter } = usePrinterContext();

  const [checkedItems, setCheckedItems] = useState<boolean[]>(items.map(i => !!i));
  const [printQuantities, setPrintQuantities] = useState<number[]>(items.map(i => i.totalQuantity));
  const [totalPrintQuantity, setTotalPrintQuantity] = useState<number>(0);

  useEffect(() => {
    let _totalPrintQuantity = 0;
    for (let i = 0; i < checkedItems.length; i++) {
      if (checkedItems[i]) _totalPrintQuantity += printQuantities[i];
    }
    setTotalPrintQuantity(_totalPrintQuantity);
  }, [printQuantities, checkedItems]);

  return (
    <>
      <Flex direction="column" gap={4} className={styles.fnskuBody}>
        {items.length === 0 && (
          <Flex
            className={styles.emptyFnskuState}
            direction="column"
            align="center"
            justify="center"
            gap={4}
          >
            <img src="/images/box-add.svg" height="100" />
            <span className={"strong"}>No products to print FNSKU labels for</span>
            <p>Add products to your shipment first to be able to print the FNSKU label for them.</p>
          </Flex>
        )}
        {items.map((item, i) => (
          <Flex key={i} align="center" gap={4} className={styles.fnskuRow}>
            <Checkbox
              value={checkedItems[i]}
              onChange={v => {
                checkedItems[i] = v;
                setCheckedItems([...checkedItems]);
              }}
            />

            <Flex align="center" gap={2}>
              <ProductView
                item={item.batchItem}
                quantity={item.totalQuantity}
                showAsin={false}
                showCondition={false}
                content2={
                  <Flex align="center" gap={2}>
                    <span className="weak">FNSKU:</span>
                    <span className="strong">{item.fnsku}</span>
                    <CopyToClipBoard value={item.fnsku} tooltipText="Copy FNSKU to clipboard"/>
                  </Flex>
                }
              />
            </Flex>

            <Flex align="center" gap={2}>
              <Button
                type="secondary"
                onClick={() => {
                  printQuantities[i] = 0;
                  setPrintQuantities([...printQuantities]);
                }}
              >
                Min
              </Button>
              <InputNumber
                value={printQuantities[i]}
                min={0}
                max={item.totalQuantity}
                style={{minWidth: "104px"}}
                onChange={v => {
                  printQuantities[i] = v;
                  setPrintQuantities([...printQuantities]);
                }}
              />
              <Button
                type="secondary"
                onClick={() => {
                  printQuantities[i] = item.totalQuantity;
                  setPrintQuantities([...printQuantities]);
                }}
              >
                Max
              </Button>
              {/* @TODO this will be added in a later diff: <Tooltip
                title={"Preview Label"}
              >
                <Button
                  type="secondary"
                  onClick={() => {

                  }}
                >
                  <IconSearch/>
                </Button>
              </Tooltip> */}
            </Flex>
          </Flex>
        ))}
      </Flex>

      <ModalFooter>
        <Flex justify="end" gap={2}>
          <Button
            size="large"
            type="secondary"
            onClick={() =>  onClose()}
          >
            Cancel
          </Button>
          <Button
            size="large"
            disabled={totalPrintQuantity === 0 || submitJobInProgress}
            onClick={() => {
              const labels: FNSKULabelSpecWithQuantity[] = [];
              for (let i = 0; i < checkedItems.length; i++) {
                if (checkedItems[i]) {
                  labels.push({
                    fnsku: items[i].fnsku,
                    productName: items[i].batchItem.name,
                    expirationDate: items[i].batchItem.expirationDate,
                    productCondition: productConditionToString(items[i].batchItem.condition),
                    quantity: printQuantities[i],
                  });
                }
              }
              const pdf = base64FNSKULabelPDFMulti(
                {
                  widthInches: preferredFNSKULabelSize?.in?.width,
                  heightInches: preferredFNSKULabelSize?.in?.height,
                },
                labels,
              );
              printBase64PDF({
                pdfDataBase64: pdf,
                printerName: preferredFNSKULabelPrinter?.name,
                size: {
                  widthInches: preferredFNSKULabelSize?.in?.width,
                  heightInches: preferredFNSKULabelSize?.in?.height
                },
                // @TODO downloadFilename
                // @TODO jobName
              });
            }}
          >
            {`Print FNSKU Labels`}
          </Button>
        </Flex>
      </ModalFooter>
    </>
  );
}

function SettingsBody() {
  const { printFNSKULabel } = usePrinterContext();

  const [customFNSKULabel, setCustomFNSKULabel] = useState<{
    fnsku: string,
    name: string,
    expirationDate?: string, // ISO date string
    condition: ProductCondition,
    quantity: number,
  }>({ fnsku: "", name: "", expirationDate: null, condition: ProductCondition.New, quantity: 1 });
  const [previewLabelBase64, setPreviewLabelBase64] = useState<string>();
  
  useEffect(() => {
    if (customFNSKULabel) {
      const base64 = base64FNSKULabelPdf(
        null,
        customFNSKULabel.fnsku,
        customFNSKULabel.name,
        productConditionToString(customFNSKULabel.condition),
        1,
        customFNSKULabel.expirationDate,
      );
      setPreviewLabelBase64(base64.data);
    }
  }, [customFNSKULabel]);

  return (
    <Flex direction="column" gap={4} className={styles.settingsBody}>
      <BoxemPrintRow/>
      <FNSKUPrinterSettings/>
      <Accordion
        title={<h3>Custom FNSKU Label</h3>}
        defaultExpanded={false}
      >
        <Flex direction="column" gap={4}>
          <div className={styles.customFNSKULabelColumns}>
            <Flex direction="column" gap={2}>
              <div className={styles.customFNSKULabelInputColumns}>
                <label className={`weak ${styles.nowrap}`}>
                  FNSKU:
                </label>
                <TextInput
                  placeholder="Type a custom FNSKU"
                  minLength={1}
                  maxLength={10}
                  value={customFNSKULabel.fnsku}
                  onChange={v => setCustomFNSKULabel({...customFNSKULabel, fnsku: v })}
                />
              </div>

              <div className={styles.customFNSKULabelInputColumns}>
                <label className={`weak ${styles.nowrap}`}>
                  Product Name:
                </label>
                <TextInput
                  placeholder="Type a custom product name"
                  minLength={1}
                  maxLength={255}
                  value={customFNSKULabel.name}
                  onChange={v => setCustomFNSKULabel({...customFNSKULabel, name: v })}
                />
              </div>
            </Flex>

            <Flex direction="column" gap={2}>
              <div className={styles.customFNSKULabelInputColumns}>
                <label className={`weak ${styles.nowrap}`}>
                  Expiration Date:
                </label>
                <DatePicker
                  showClearButton
                  value={customFNSKULabel.expirationDate ? new Date(customFNSKULabel.expirationDate) : null}
                  onChange={v => setCustomFNSKULabel({...customFNSKULabel, expirationDate: v?.toISOString() })}
                />
              </div>

              <div className={styles.customFNSKULabelInputColumns}>
                <label className={`weak ${styles.nowrap}`}>
                  Condition:
                </label>
                <Select
                  fullWidth
                  options={[
                    ProductCondition.New,
                    ProductCondition.NewOpenBox,
                    ProductCondition.NewOEM,

                    ProductCondition.Refurbished,

                    ProductCondition.UsedLikeNew,
                    ProductCondition.UsedVeryGood,
                    ProductCondition.UsedGood,
                    ProductCondition.UsedAcceptable,

                    ProductCondition.CollectibleLikeNew,
                    ProductCondition.CollectibleVeryGood,
                    ProductCondition.CollectibleGood,
                    ProductCondition.CollectibleAcceptable,

                    ProductCondition.Club,
                  ]}
                  optionRender={option => {
                    switch (option) {
                      case ProductCondition.Refurbished:
                      case ProductCondition.UsedLikeNew:
                      case ProductCondition.CollectibleLikeNew:
                      case ProductCondition.Club:
                        return (
                          <Flex direction="column" gap={1} style={{width:"100%"}}>
                            <Divider margin="0 0 0.5rem 0" />
                            {productConditionToString(option, false)}
                          </Flex>
                        );
                    }

                    return productConditionToString(option, false);
                  }}
                  selectionRender={option => productConditionToString(option, false)}
                  value={customFNSKULabel.condition}
                  onChange={v => setCustomFNSKULabel({...customFNSKULabel, condition: v})}
                />
              </div>
            </Flex>
          </div>

          {previewLabelBase64 && (
            <embed
              width="100%"
              style={{backgroundColor: "rgb(212 212 215)"}}
              src={"data:application/pdf;base64," + previewLabelBase64}
              type="application/pdf"
            >
            </embed>
          )}

          <Divider margin="0 0 0.5rem 0"/>

          <Flex justify="end" align="center">
            <Button
              type="primary"
              size="large"
              disabled={false}
              onClick={() => printFNSKULabel(customFNSKULabel.fnsku, customFNSKULabel.name, customFNSKULabel.condition, customFNSKULabel.quantity, customFNSKULabel.expirationDate)}
            >
              Print Custom FNSKU Label
            </Button>
          </Flex>
        </Flex>
      </Accordion>
    </Flex>
  );
}

const rerenderLabelPreviewDebounce = { id: 0 };
function OtherLabelsBody() {
  const { printers, connectionState, submitJobInProgress, useBoxemPrint, printBase64PDF } = usePrinterContext();

  const otherLabelRadio = "other-label-radio";

  const [radio, setRadio] = useState<OtherLabelsTab>("sold_as_set");
  const [previewLabelBase64, setPreviewLabelBase64] = useState<string>();
  const [preferredPrinter, setPreferredPrinter] = useState<PrinterDetails>();
  const [preferredLabelSize, setPreferredLabelSize] = useState<PaperSize>();
  const [copies, setCopies] = useState<number>(1);

  const [customLabelConfig, setCustomLabelConfig] = useState<SimpleLabelGenerationOptions>({
    widthInches: 4,
    heightInches: 4,
    text: "",
    textColor: [0, 0, 0],
    bold: true,
    lineHeightMultiplier: 1.5,
    borderColor: [0, 0, 0],
    copies: 1,
  });

  const rerenderLabelPreview = () => {
    let labelWidth = 4, labelHeight = 4; // in inches
    if (useBoxemPrint && !!preferredLabelSize) {
      labelWidth = preferredLabelSize.in.width;
      labelHeight = preferredLabelSize.in.height;
    }
    let _previewLabelBase64;  
    switch (radio) {
      case "sold_as_set":
        _previewLabelBase64 = base64SoldAsSetStickerPDF(labelWidth, labelHeight, 1);
        break;
      case "suffocation":
        _previewLabelBase64 = base64SSuffocationStickerPDF(labelWidth, labelHeight, 1);
        break;
      case "fragile":
        _previewLabelBase64 = base64FragileLabelPDF(labelWidth, labelHeight, 1);
        break;
      case "custom":
        _previewLabelBase64 = base64SimpleLabelPDF(customLabelConfig);
        break;
    }
    setPreviewLabelBase64(_previewLabelBase64);
  }

  const normalizedRGBToHex = (v: [number, number, number]): string => {
    const c = `#${Math.floor(v[0]*255).toString(16)}${Math.floor(v[1]*255).toString(16)}${Math.floor(v[2]*255).toString(16)}`;
    return c;
  }

  const hexToNormalizedRGB = (v: string): [number, number, number] => {
    const r = parseInt(v.substring(1, 3), 16) / 255;
    const g = parseInt(v.substring(3, 5), 16) / 255;
    const b = parseInt(v.substring(5, 7), 16) / 255;
    return [r,g,b];
  }

  const labelKindToDisplayName = (r: OtherLabelsTab): string => {
    switch (r) {
      case "sold_as_set": return "Sold As Set Label";
      case "suffocation": return "Suffocation Warning Label";
      case "fragile":     return "Fragile Warning Label";
      case "custom":      return "Custom Label";
    }
  }

  useEffect(() => {
    clearTimeout(rerenderLabelPreviewDebounce.id);
    rerenderLabelPreviewDebounce.id = window.setTimeout(() => {
      rerenderLabelPreview();
    }, 100);
  }, [radio, preferredLabelSize, preferredPrinter, customLabelConfig]);

  useEffect(() => {
    const _preferredPrinterName = window.localStorage.getItem(LocalStorageKeys.preferredOtherLabelsPrinter);
    const _preferredLabelSize = getLocalStorageItem<PaperSize>(LocalStorageKeys.preferredOtherLabelsLabelSize);
    if (!!_preferredPrinterName) {
      const printer = printers.find(p => p.name === _preferredPrinterName);
      if (printer) {
        setPreferredPrinter(printer);

        if (!!_preferredLabelSize) {
          for (const labelSize of printer.sizes) {
            if (labelSize.name === _preferredLabelSize?.name) {
              setPreferredLabelSize(_preferredLabelSize);
              setCustomLabelConfig({...customLabelConfig, widthInches: _preferredLabelSize.in.width, heightInches: _preferredLabelSize.in.height });
              break;
            }
          }
        }
      }
    }
  }, []);

  return (
    <Flex direction="column" gap={4} className={styles.otherLabelsBody}>
      <label className="strong">Label/Sticker Type:</label>
      <Flex gap={4} align="center">
        <RadioInput
          label="Sold as Set"
          name={otherLabelRadio}
          value={"sold_as_set"}
          checked={radio === "sold_as_set"}
          onChange={() => setRadio("sold_as_set")}
        />

        <RadioInput
          label="Suffocation"
          name={otherLabelRadio}
          value={"suffocation"}
          checked={radio === "suffocation"}
          onChange={() => setRadio("suffocation")}
        />
          
        <RadioInput
          label="Fragile"
          name={otherLabelRadio}
          value={"fragile"}
          checked={radio === "fragile"}
          onChange={() => setRadio("fragile")}
        />

        <RadioInput
          label="Custom"
          name={otherLabelRadio}
          value={"custom"}
          checked={radio === "custom"}
          onChange={() => setRadio("custom")}
        />
      </Flex>

      <Divider margin="0.25rem"/>

      {!useBoxemPrint && (
        <Alert
          type="info"
          message={
            <Flex direction="column" gap={2}>
              <p>
                Enable 'Use Boxem Print' in printer settings first if you want to send these labels directly to your printer, speeding up your workflow.
              </p>
              {connectionState !== QzConnectionState.connected && (
                <p>
                  You'll need to install Boxem Print first:
                  <a target="_blank" href="/settings/printing">Open Printer Settings in a New Tab</a>
                </p>
              )}
            </Flex>
          }
        />
      )}

      {useBoxemPrint && (
        <Flex direction="column" className={styles.formGroup} gap={4}>
          <Flex gap={2}>
            <Select
              fullWidth         
              options={printers}
              disabled={connectionState !== QzConnectionState.connected}
              value={preferredPrinter}
              placeholder={
                <Flex gap={2}>
                  <IconPrintGoogle />
                  Select a printer
                </Flex>
              }
              onChange={(value: PrinterDetails) => {
                const printer = printers.find((printer) => printer.name === value?.name);
                setPreferredPrinter(printer);
                window.localStorage.setItem(LocalStorageKeys.preferredOtherLabelsPrinter, value?.name);
              }}
              optionRender={option => {
                return (
                  <Flex gap={2}>
                    <IconPrintGoogle />
                    {option.name}
                  </Flex>
                );
              }}
            />
            {!!preferredPrinter && (
              <Button
                type="text"
                onClick={() => {
                  setPreferredPrinter(null)
                  window.localStorage.removeItem(LocalStorageKeys.preferredOtherLabelsPrinter);
                }}
              >
                Clear
              </Button>
            )}
          </Flex>

          <Flex gap={2} align="center">
            {!!preferredPrinter && (
              <>
                <span style={{minWidth:"max-content"}}>Label size:</span>
                <Select
                  fullWidth
                  disabled={submitJobInProgress}
                  placeholder="Select a preferred label size"
                  optionRender={name => {
                    const size = preferredPrinter.sizes.find(_s => _s.name === name);
                    return ( 
                      <Flex style={{width:"100%"}} justify="space-between" align="center">
                        <span>{name}</span>

                        <span>{size.in.width.toLocaleString("en-US", { maximumFractionDigits: 2 })}x{size.in.height.toLocaleString("en-US", { maximumFractionDigits: 2 })}"</span>
                      </Flex>
                    );
                  }}
                  options={preferredPrinter.sizes?.map(s => s.name) ?? []}
                  value={preferredLabelSize?.name}
                  onChange={name => {
                    const size = preferredPrinter.sizes.find(_s => _s.name === name);
                    setCustomLabelConfig({...customLabelConfig, widthInches: size?.in?.width ?? 4, heightInches: size?.in?.height ?? 4 });
                    setPreferredLabelSize(size);
                    setLocalStorageItem(LocalStorageKeys.preferredOtherLabelsLabelSize, size);
                  }}
                />
                {!!preferredLabelSize && (
                  <Button
                    disabled={submitJobInProgress}
                    type="text"
                    onClick={() => {
                      setPreferredLabelSize(null);
                      window.localStorage.removeItem(LocalStorageKeys.preferredOtherLabelsLabelSize);
                    }}
                  >
                    Clear
                  </Button>
                )}
              </>
            )}
          </Flex>
        </Flex>
      )}

      {radio === "custom" && (
        <>
          {(!useBoxemPrint || connectionState !== QzConnectionState.connected) && (
            <Flex align="center" gap={2}>
              <Flex align="center" gap={2}>
                <label htmlFor="labelWidth">Width (in):</label>
                <InputNumber
                  inputId="labelWidth"
                  value={customLabelConfig.widthInches}  
                  onChange={v => setCustomLabelConfig({...customLabelConfig, widthInches: v })}
                  min={1}
                  max={20}
                /> 
              </Flex>

              <Flex align="center" gap={2}>
                <label htmlFor="labelHeight">Height (in):</label>
                <InputNumber
                  inputId="labelHeight"
                  value={customLabelConfig.heightInches}  
                  onChange={v => setCustomLabelConfig({...customLabelConfig, heightInches: v })}
                  min={1}
                  max={20}
                /> 
              </Flex>
            </Flex>
          )}

          <Flex direction="column" gap={1}>
            <label htmlFor="customLabelText">Label text:</label>
            <Textarea
              inputId="customLabelText"
              resizable={false}
              placeholder="type the multi-line text you want on your custom label here!"
              value={customLabelConfig.text ?? ""}
              onChange={v => setCustomLabelConfig({...customLabelConfig, text: v })}
              maxLength={1024}
            />
          </Flex>

          <Flex align="center" gap={4}>
            <Flex align="center" gap={2}>
              <label htmlFor="customLabelTextColor">Text Color:</label>
              <input
                id="customLabelTextColor"
                type="color"
                value={normalizedRGBToHex(customLabelConfig.textColor)}
                onChange={e => {
                  setCustomLabelConfig({...customLabelConfig, textColor: hexToNormalizedRGB(e.target.value) });
                }}
              />
            </Flex>

            {/* <Flex align="center" gap={2}>
              <label htmlFor="labelTextBold">Bold Text:</label>
              <Checkbox
                id="labelTextBold"
                value={customLabelConfig.bold}
                onChange={v => setCustomLabelConfig({...customLabelConfig, bold: v })}
              />
            </Flex> */}

            <Flex align="center" gap={2}>
              <label htmlFor="customLabelLineHeight">Line Height:</label>
              <InputNumber
                inputId="customLabelLineHeight"
                showControls={false}
                value={customLabelConfig.lineHeightMultiplier}  
                onChange={v => setCustomLabelConfig({...customLabelConfig, lineHeightMultiplier: v })}
                min={1}
                max={20}
              /> 
            </Flex>
          </Flex>

          <Flex align="center" gap={4}>
            <Flex align="center" gap={2}>
              <label htmlFor="customLabelBorder">Border:</label>
              <Checkbox
                id="customLabelBorder"
                value={!!customLabelConfig.borderColor || customLabelConfig.borderColor === undefined}
                onChange={v => setCustomLabelConfig({...customLabelConfig, borderColor: v ? [0, 0, 0] : null })}
              />
            </Flex>

            {customLabelConfig.borderColor && (
              <Flex align="center" gap={2}>
                <label htmlFor="customLabelBorderColor">Border Color:</label>
                <input
                  id="customLabelBorderColor"
                  type="color"
                  value={normalizedRGBToHex(customLabelConfig.borderColor)}
                  onChange={e => {
                    setCustomLabelConfig({...customLabelConfig, borderColor: hexToNormalizedRGB(e.target.value) });
                  }}
                />
              </Flex>
            )}
          </Flex>
        </>
      )}

      {previewLabelBase64 && (
        <embed
          width="100%"
          style={{marginBottom: "1rem", height: "20rem", backgroundColor: "rgb(212 212 215)"}}
          src={"data:application/pdf;base64," + previewLabelBase64}
          type="application/pdf"
        >
        </embed>
      )}

      <Flex justify="end" align="center" gap={4}>
        <Flex align="center" gap={2}>
          <label>Quantity:</label>
          <InputNumber
            value={copies}
            max={50}
            onChange={v => {
              setCopies(v);
              if (radio === "custom") {
                setCustomLabelConfig({...customLabelConfig, copies: v });
              }
            }}
          />
        </Flex>

        <Flex align="center" gap={2}>
          <Button
            type="primary"
            disabled={submitJobInProgress}
            onClick={() => {
              let labelWidth = 4, labelHeight = 4; // in inches
              if (useBoxemPrint && !!preferredLabelSize) {
                labelWidth = preferredLabelSize.in.width;
                labelHeight = preferredLabelSize.in.height;
              }
              let actualPDF;  
              switch (radio) {
                case "sold_as_set":
                  actualPDF = base64SoldAsSetStickerPDF(labelWidth, labelHeight, copies);
                  break;
                case "suffocation":
                  actualPDF = base64SSuffocationStickerPDF(labelWidth, labelHeight, copies);
                  break;
                case "fragile":
                  actualPDF = base64FragileLabelPDF(labelWidth, labelHeight, copies);
                  break;
                case "custom":
                  actualPDF = base64SimpleLabelPDF(customLabelConfig);
                  break;
              }
              printBase64PDF({
                pdfDataBase64: actualPDF,
                printerName: preferredPrinter?.name,
              });
            }}
          >
            Print {labelKindToDisplayName(radio)}
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
}

// @FRAGILE - tied to the 'values' of the radio buttons above
type OtherLabelsTab = "sold_as_set" | "suffocation" | "fragile" | "custom";
